import { useMutation } from "@tanstack/react-query";
import { ToastType } from "@/components/common/toast/Toast.tsx";
import { commonService } from "@/services/commonService.ts";
import { isAndroid, isIPad13 } from "react-device-detect";
import { PDF_GENERATION_FAILED, SOMETHING_WENT_WRONG } from "@/constants/strings";
import html2pdf from "html2pdf.js";
declare global {
  interface Window {
    _printPopStateHandlerAttached?: boolean;
  }
}
let openIframePrint = false;

export const usePrintDeliveryDocketMutation = (showToast: any) => {
  return useMutation({
    mutationFn: (deliveryDocketNo: string) => {
      if (isIPad13) {
        return commonService.printDeliveryDocketIOS({ deliveryDocketNo });
      } else if (isAndroid) {
        return commonService.printDeliveryDocketAndroid({ deliveryDocketNo });
      }
      return commonService.printDeliveryDocket(deliveryDocketNo);
    },
    onSuccess: async (data: any, deliveryDocketNo: string) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(data, "text/html");

      // Format Delivery Date
      const deliveryDateEl = doc.querySelector("#info .item .info-value");
      if (deliveryDateEl?.textContent?.trim()) {
        const deliveryDate = new Date(deliveryDateEl.textContent.trim());
        const formatted = `${deliveryDate.getDate().toString().padStart(2, "0")}/${(deliveryDate.getMonth() + 1)
          .toString()
          .padStart(
            2,
            "0"
          )}/${deliveryDate.getFullYear()} ${deliveryDate.getHours().toString().padStart(2, "0")}:${deliveryDate.getMinutes().toString().padStart(2, "0")}`;
        deliveryDateEl.textContent = formatted;
      }

      // Format all Scan Time
      const scanTimeCells = doc.querySelectorAll("#table3 tbody tr td:first-child");
      scanTimeCells.forEach(cell => {
        if (cell.textContent?.trim()) {
          const scanTimeDate = new Date(cell.textContent.trim());
          const formatted = `${scanTimeDate.getDate().toString().padStart(2, "0")}/${(scanTimeDate.getMonth() + 1)
            .toString()
            .padStart(
              2,
              "0"
            )}/${scanTimeDate.getFullYear()} ${scanTimeDate.getHours().toString().padStart(2, "0")}:${scanTimeDate.getMinutes().toString().padStart(2, "0")}`;
          cell.textContent = formatted;
        }
      });

      const fileName = `delivery_docket_${deliveryDocketNo}.pdf`;
      const updatedHtml = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
      const blob = new Blob([updatedHtml], { type: "text/html" });
      const url = URL.createObjectURL(blob);

      if (isAndroid) {
        try {
          // Create hidden container
          const container = document.createElement("div");
          container.style.position = "fixed";
          container.style.top = "-9999px";
          container.style.left = "-9999px";
          container.style.width = "794px";
          container.style.minHeight = "1123px";
          container.style.background = "#ffffff";
          container.style.padding = "0";
          container.style.margin = "0";
          container.style.zoom = "1";
          container.style.overflow = "hidden";
          container.innerHTML = updatedHtml;

          const style = document.createElement("style");
          style.innerHTML = `thead { display: table-header-group; }`;
          container.appendChild(style);

          document.body.appendChild(container);

          const target = container.querySelector(".body-a4");

          if (!target) throw new Error("Missing .body-a4");

          const opt = {
            margin: [10, 10, 10, 10],
            filename: fileName,
            image: { type: "jpeg", quality: 1 },
            html2canvas: {
              scale: 2.5,
              useCORS: true,
              allowTaint: true,
              scrollY: 0,
              windowWidth: 794 + 80
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
          };

          const pdfBlob = await html2pdf().set(opt).from(target).outputPdf("blob");

          const blobUrl = URL.createObjectURL(pdfBlob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = fileName;
          link.click();

          setTimeout(() => {
            document.body.removeChild(container);
            URL.revokeObjectURL(blobUrl);
          }, 100);
        } catch (e) {
          showToast(ToastType.error, PDF_GENERATION_FAILED);
        }
      } else {
        try {
          if (isIPad13) {
            const PRINT_HASH = "#print";
            openIframePrint = false;
            if (location.hash.includes("print")) {
              history.replaceState({}, "", location.pathname + location.search);
            }
            history.pushState({ printMode: true }, "", location.href.split("#")[0] + PRINT_HASH);

            const removePrintWrapper = () => {
              const pw = document.getElementById("print-wrapper");
              if (pw) pw.remove();
              if (location.hash.includes("print")) {
                history.replaceState({}, "", location.pathname + location.search);
              }
              location.reload();
            };

            const onPopState = () => {
              if (!location.hash.includes("print")) {
                removePrintWrapper();
                detachListeners();
              }
            };

            const onPageShow = (e: any) => {
              if (e && e.persisted && !location.hash.includes("print")) {
                removePrintWrapper();
                detachListeners();
              }
            };

            const detachListeners = () => {
              window.removeEventListener("popstate", onPopState);
              window.removeEventListener("pageshow", onPageShow);
              window._printPopStateHandlerAttached = false;
            };

            if (!window._printPopStateHandlerAttached) {
              window.addEventListener("popstate", onPopState);
              window.addEventListener("pageshow", onPageShow);
              window._printPopStateHandlerAttached = true;
            }

            const printWrapper = document.createElement("div");
            printWrapper.id = "print-wrapper";
            Object.assign(printWrapper.style, {
              position: "fixed",
              top: "0",
              left: "0",
              width: "100vw",
              height: "100vh",
              backgroundColor: "white",
              zIndex: "9999"
            });

            const iframe = document.createElement("iframe");
            iframe.style.width = "100%";
            iframe.style.height = "100%";
            iframe.style.border = "none";

            iframe.onload = () => {
              if (!openIframePrint) {
                openIframePrint = true;
                if (iframe.contentWindow) {
                  iframe.contentWindow.focus();
                  iframe.contentWindow.print();
                }
              }
            };
            iframe.srcdoc = updatedHtml + `<!-- ${Date.now()}_${Math.random()} -->`;
            printWrapper.appendChild(iframe);
            document.body.appendChild(printWrapper);
          } else {
            const deliveryDocketsPage = document.createElement("a");
            deliveryDocketsPage.href = url;
            deliveryDocketsPage.target = "_blank";
            deliveryDocketsPage.click();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
          }
        } catch (e: any) {
          showToast(ToastType.error, "Popup blocked or print view failed.");
        }
      }
    },
    onError: () => {
      showToast(ToastType.error, SOMETHING_WENT_WRONG);
    }
  });
};
